import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../environments/environment';
import { getMappedRoleForUser } from '../../../../public/assets/dashboard-config/role-map/role-map';
import { RoleMenu } from '../../../../public/assets/dashboard-config/role-map/user-role';

@Injectable({
  providedIn: 'root',
})
export class KeycloakInitService {
  private userEmail: string | null = null;
  private userRole: string | null = null;
  private allowedRoutes: string[] = [];
  private accessResolved = false;

  constructor(private keycloakService: KeycloakService) {}

  async initKeycloak(): Promise<void> {
    try {
      await this.keycloakService.init({
        config: {
          url: environment.KEYCLOAK_URL,
          realm: environment.KEYCLOAK_REALM,
          clientId: environment.KEYCLOAK_CLIENT_ID,
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri:
            window.location.origin + '/assets/silent-check-sso.html',
          pkceMethod: 'S256',
        },
        enableBearerInterceptor: true,
        bearerPrefix: 'Bearer',
      });

      const isLoggedIn = await this.keycloakService.isLoggedIn();
      if (!isLoggedIn) {
        this.clearStoredAccess();
        return Promise.resolve();
      }

      await this.loadUserAccess();
      return Promise.resolve();
    } catch (error) {
      console.error('Keycloak initialization failed', error);
      this.clearStoredAccess();
      throw error;
    }
  }

  async getUserEmail(): Promise<string | null> {
    if (this.userEmail) {
      return this.userEmail;
    }

    const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed as
      | Record<string, unknown>
      | undefined;

    const profileEmail = typeof tokenParsed?.['email'] === 'string'
      ? tokenParsed['email']
      : null;

    if (profileEmail?.trim()) {
      this.userEmail = profileEmail.trim().toLowerCase();
      return this.userEmail;
    }

    try {
      const userProfile = await this.keycloakService.loadUserProfile();
      const resolvedEmail = userProfile?.email?.trim().toLowerCase() || null;
      this.userEmail = resolvedEmail;
      return this.userEmail;
    } catch (error) {
      console.warn('Unable to load Keycloak user profile for email mapping.', error);
      return null;
    }
  }

  async getUserRole(): Promise<string | null> {
    if (this.accessResolved) {
      return this.userRole;
    }

    await this.loadUserAccess();
    return this.userRole;
  }

  async getAllowedRoutes(): Promise<string[]> {
    if (this.accessResolved) {
      return [...this.allowedRoutes];
    }

    await this.loadUserAccess();
    return [...this.allowedRoutes];
  }

  async canAccessRoute(url: string): Promise<boolean> {
    const normalizedUrl = this.normalizeRoute(url);

    if (normalizedUrl === '/access-denied') {
      return true;
    }

    const allowedRoutes = await this.getAllowedRoutes();
    return allowedRoutes.some((route) => this.normalizeRoute(route) === normalizedUrl);
  }

  async getFirstAllowedRoute(): Promise<string> {
    const allowedRoutes = await this.getAllowedRoutes();
    return allowedRoutes[0] || '/access-denied';
  }

  private async loadUserAccess(): Promise<void> {
    if (this.accessResolved) {
      return;
    }

    const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed as
      | Record<string, unknown>
      | undefined;

    const email = await this.getUserEmail();
    const wwid =
      (typeof tokenParsed?.['preferred_username'] === 'string'
        ? tokenParsed['preferred_username']
        : null) ||
      (typeof tokenParsed?.['upn'] === 'string' ? tokenParsed['upn'] : null) ||
      null;

    const role = getMappedRoleForUser({
      email,
      wwid,
      username: wwid,
    });

    this.userRole = role;
    this.allowedRoutes = role && Array.isArray(RoleMenu[role]) ? [...RoleMenu[role]] : [];
    this.accessResolved = true;
    this.storeResolvedAccess();
  }

  private storeResolvedAccess(): void {
    localStorage.setItem('mappedUserEmail', this.userEmail ?? '');
    localStorage.setItem('mappedUserRole', this.userRole ?? '');
    localStorage.setItem('allowedRoutes', JSON.stringify(this.allowedRoutes));
  }

  private clearStoredAccess(): void {
    this.userEmail = null;
    this.userRole = null;
    this.allowedRoutes = [];
    this.accessResolved = false;
    localStorage.removeItem('mappedUserEmail');
    localStorage.removeItem('mappedUserRole');
    localStorage.removeItem('allowedRoutes');
  }

  private normalizeRoute(url: string): string {
    const cleanUrl = (url || '').split('?')[0].split('#')[0].trim().toLowerCase();
    const normalized = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;

    if (normalized === '/' || normalized === '') {
      return '/';
    }

    return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
  }
}
